// src/services/azureCostEstimatesService.js
// Azure Table Storage Service für Kostenschätzungs-Dossiers
import { ensureTableExists } from "./azureTableUtils";

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "costEstimates",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const baseUrl = () =>
  `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}`;

const cleanValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
};

// Alle Kostenschätzungs-Dossiers laden
export const getAllEstimates = async () => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    let allEstimates = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `${baseUrl()}?${AZURE_CONFIG.sasToken}`;

      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Azure Error:", errorText);
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();
      allEstimates = allEstimates.concat(data.value || []);

      nextPartitionKey = response.headers.get(
        "x-ms-continuation-NextPartitionKey"
      );
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    return allEstimates
      .map((item) => ({
        id: item.RowKey,
        name: item.Name || "",
        client: item.Client || "",
        date: item.Date || "",
        linkedProjectCode: item.LinkedProjectCode || "",
        phases: item.Phases ? JSON.parse(item.Phases) : [],
        currentPhase: item.CurrentPhase || "",
        createdBy: item.CreatedBy || "",
        createdAt: item.CreatedAt || "",
        updatedAt: item.UpdatedAt || "",
        deletedAt: item.DeletedAt || "",
        editors: item.Editors ? JSON.parse(item.Editors) : [],
        // Objektgliederung (US-KOST-023): erscheint auf allen Zahlungsanweisungen
        // des Dossiers; gepflegt in der Sidebar der Kostenkontrolle.
        objektgliederung: item.Objektgliederung || "",
      }))
      .filter((est) => !est.deletedAt);
  } catch (error) {
    console.error("Load Estimates Error:", error);
    return [];
  }
};

// Kostenschätzungs-Dossier speichern (neu anlegen)
export const saveEstimate = async (estimateData) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const estimateId = crypto.randomUUID();
    const now = new Date().toISOString();

    const entity = {
      PartitionKey: "MF",
      RowKey: estimateId,
      Name: cleanValue(estimateData.name),
      Client: cleanValue(estimateData.client),
      Date: cleanValue(estimateData.date),
      LinkedProjectCode: cleanValue(estimateData.linkedProjectCode),
      Phases: JSON.stringify(estimateData.phases || []),
      CurrentPhase: cleanValue(estimateData.currentPhase),
      CreatedBy: cleanValue(estimateData.createdBy),
      CreatedAt: now,
    };

    const url = `${baseUrl()}(PartitionKey='MF',RowKey='${estimateId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error Details:", errorText);
      throw new Error(`Azure Save Error: ${response.status} - ${errorText}`);
    }

    return {
      id: estimateId,
      name: estimateData.name,
      client: estimateData.client || "",
      date: estimateData.date || "",
      linkedProjectCode: estimateData.linkedProjectCode || "",
      phases: estimateData.phases || [],
      currentPhase: estimateData.currentPhase || "",
      createdBy: estimateData.createdBy || "",
      createdAt: now,
    };
  } catch (error) {
    console.error("Save Estimate Error:", error);
    throw error;
  }
};

// Kostenschaetzungs-Dossier aktualisieren.
// MERGE statt PUT: nur angegebene Felder werden ueberschrieben, damit
// Editors (via trackEditor) und DeletedAt (via softDeleteEstimate) erhalten
// bleiben. Zuvor hat PUT das gesamte Entity ersetzt und diese Felder geloescht.
export const updateEstimate = async (estimateId, estimateData) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const now = new Date().toISOString();

    const entity = {
      PartitionKey: "MF",
      RowKey: estimateId,
      Name: cleanValue(estimateData.name),
      Client: cleanValue(estimateData.client),
      Date: cleanValue(estimateData.date),
      LinkedProjectCode: cleanValue(estimateData.linkedProjectCode),
      Phases: JSON.stringify(estimateData.phases || []),
      CurrentPhase: cleanValue(estimateData.currentPhase),
      CreatedBy: cleanValue(estimateData.createdBy),
      CreatedAt: cleanValue(estimateData.createdAt),
      UpdatedAt: now,
    };

    const url = `${baseUrl()}(PartitionKey='MF',RowKey='${estimateId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "MERGE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
        "If-Match": "*",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error Details:", errorText);
      throw new Error(`Azure Update Error: ${response.status} - ${errorText}`);
    }

    return {
      id: estimateId,
      ...estimateData,
      updatedAt: now,
    };
  } catch (error) {
    console.error("Update Estimate Error:", error);
    throw error;
  }
};

// Objektgliederung des Dossiers setzen (US-KOST-023, ADR 0064). Eigener MERGE
// nur dieses Feldes: updateEstimate() round-trippt bewusst nur seine festen
// Felder — wuerde Objektgliederung dort mitgefuehrt, loeschte jeder Aufrufer
// ohne das Feld den Wert wieder (vgl. Kommentar zu __ctrlmeta__ im Provider).
export const updateEstimateObjektgliederung = async (estimateId, objektgliederung) => {
  await ensureTableExists(AZURE_CONFIG.tableName);
  const entity = {
    PartitionKey: "MF",
    RowKey: estimateId,
    Objektgliederung: cleanValue(objektgliederung),
    UpdatedAt: new Date().toISOString(),
  };
  const url = `${baseUrl()}(PartitionKey='MF',RowKey='${estimateId}')?${AZURE_CONFIG.sasToken}`;
  const response = await fetch(url, {
    method: "MERGE",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
      "If-Match": "*",
    },
    body: JSON.stringify(entity),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Azure Update Error: ${response.status} - ${errorText}`);
  }
  return entity.Objektgliederung;
};

// Dossier soft-delete (marks as deleted, retained for 30 days)
export const softDeleteEstimate = async (estimateId) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const now = new Date().toISOString();
    const url = `${baseUrl()}(PartitionKey='MF',RowKey='${estimateId}')?${AZURE_CONFIG.sasToken}`;

    // MERGE to only update DeletedAt field
    const response = await fetch(url, {
      method: "MERGE",
      headers: {
        "Content-Type": "application/json",
        "If-Match": "*",
      },
      body: JSON.stringify({
        PartitionKey: "MF",
        RowKey: estimateId,
        DeletedAt: now,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure Delete Error: ${response.status} - ${errorText}`);
    }
  } catch (error) {
    console.error("Soft Delete Estimate Error:", error);
    throw error;
  }
};

// Dossier duplizieren
export const duplicateEstimate = async (sourceEstimate, createdBy) => {
  try {
    const newId = crypto.randomUUID();
    const now = new Date().toISOString();

    const saved = await saveEstimate({
      name: `${sourceEstimate.name} (Kopie)`,
      client: sourceEstimate.client,
      date: sourceEstimate.date,
      linkedProjectCode: sourceEstimate.linkedProjectCode,
      phases: sourceEstimate.phases,
      currentPhase: sourceEstimate.currentPhase,
      createdBy,
    });

    return saved;
  } catch (error) {
    console.error("Duplicate Estimate Error:", error);
    throw error;
  }
};

// Track editor activity on a dossier
export const trackEditor = async (estimateId, userName) => {
  if (!userName) return;
  try {
    // Fetch current editors
    const url = `${baseUrl()}(PartitionKey='MF',RowKey='${estimateId}')?${AZURE_CONFIG.sasToken}`;
    const getResp = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });
    if (!getResp.ok) return;

    const entity = await getResp.json();
    const editors = entity.Editors ? JSON.parse(entity.Editors) : [];
    // editors = [{ name, lastActive }]

    const now = new Date().toISOString();
    const existing = editors.findIndex((e) => e.name === userName);
    if (existing >= 0) {
      editors[existing].lastActive = now;
    } else {
      editors.push({ name: userName, lastActive: now });
    }

    await fetch(url, {
      method: "MERGE",
      headers: {
        "Content-Type": "application/json",
        "If-Match": "*",
      },
      body: JSON.stringify({
        PartitionKey: "MF",
        RowKey: estimateId,
        Editors: JSON.stringify(editors),
        UpdatedAt: now,
      }),
    });
  } catch (error) {
    console.error("Track editor error:", error);
  }
};

// Konfiguration aktualisieren
export const updateConfig = (newConfig) => {
  Object.assign(AZURE_CONFIG, newConfig);
};
